import React from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { OWNERSHIP_STATUS_OPTIONS } from "../../constants/ownershipStatus";
import { DisplayPreferences } from "../../hooks/useTenantDisplayPreferences";
import { Operator } from "../../types/operator";
import { findOperatorName } from "../../utils/operatorUtils";
import { tenantManagementTextFieldSx } from "../tenant-management/tenantManagementStyles";

type OwnershipFormFieldsProps = {
  preferences: DisplayPreferences;
  ownershipStatus: number | "" | null | undefined;
  owner: string;
  operatorId?: string;
  operatorName?: string | null;
  operators?: Operator[];
  operatorsLoading?: boolean;
  onOwnershipStatusChange: (value: number | "") => void;
  onOwnerChange: (value: string) => void;
  onOperatorIdChange?: (value: string) => void;
  showOperator?: boolean;
  hideOwner?: boolean;
};

const selectSx = {
  color: "#fff",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#555" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(19 101% 51% / 1)" },
  "& .MuiSvgIcon-root": { color: "#fff" },
};

const menuProps = {
  slotProps: {
    paper: {
      sx: {
        zIndex: 1500,
        backgroundColor: "#222",
        color: "#fff",
        "& .MuiMenuItem-root": {
          color: "#fff",
          "&:hover": {
            backgroundColor: "hsl(19 101% 51% / 0.2)",
          },
          "&.Mui-selected": {
            backgroundColor: "hsl(19 101% 51% / 0.3)",
          },
        },
      },
    },
  },
};

export const OwnershipFormFields: React.FC<OwnershipFormFieldsProps> = ({
  preferences,
  ownershipStatus,
  owner,
  operatorId = "",
  operatorName = null,
  operators = [],
  operatorsLoading = false,
  onOwnershipStatusChange,
  onOwnerChange,
  onOperatorIdChange,
  showOperator = false,
  hideOwner = false,
}) => {
  const showSection =
    preferences.showOwnershipStatus ||
    (!hideOwner && preferences.showOwner) ||
    (showOperator && preferences.showOperator);

  if (!showSection) return null;

  const statusSelectValue =
    ownershipStatus === "" ||
    ownershipStatus === null ||
    ownershipStatus === undefined
      ? ""
      : String(ownershipStatus);

  const operatorSelectValue = operatorId
    ? String(operatorId).trim().toLowerCase()
    : "";

  return (
    <Box sx={{ gridColumn: "1 / -1" }}>
      <Typography
        variant="subtitle2"
        sx={{ color: "hsl(19 101% 51% / 1)", mt: 1, mb: 1 }}
      >
        Ownership
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        {showOperator && preferences.showOperator && onOperatorIdChange && (
          <FormControl fullWidth>
            <InputLabel
              id="ownership-operator-label"
              sx={{ color: "#fff" }}
              shrink
            >
              Operator
            </InputLabel>
            <Select
              labelId="ownership-operator-label"
              label="Operator"
              displayEmpty
              value={operatorSelectValue}
              onChange={(e) => onOperatorIdChange(String(e.target.value))}
              disabled={operatorsLoading}
              sx={selectSx}
              MenuProps={menuProps}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography component="span" sx={{ color: "#888" }}>
                      Select operator
                    </Typography>
                  );
                }
                return (
                  findOperatorName(operators, selected, operatorName) ||
                  "Selected operator"
                );
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {operators.map((op) => (
                <MenuItem key={op.id} value={op.id}>
                  {op.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {preferences.showOwnershipStatus && (
          <FormControl fullWidth>
            <InputLabel
              id="ownership-status-label"
              sx={{ color: "#fff" }}
              shrink
            >
              Ownership Status
            </InputLabel>
            <Select
              labelId="ownership-status-label"
              label="Ownership Status"
              displayEmpty
              value={statusSelectValue}
              onChange={(e) => {
                const v = e.target.value;
                onOwnershipStatusChange(v === "" ? "" : Number(v));
              }}
              sx={selectSx}
              MenuProps={menuProps}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography component="span" sx={{ color: "#888" }}>
                      Select status
                    </Typography>
                  );
                }
                const label = OWNERSHIP_STATUS_OPTIONS.find(
                  (opt) => String(opt.value) === String(selected)
                )?.label;
                return label ?? "Selected status";
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {OWNERSHIP_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {preferences.showOwner && !hideOwner && (
          <TextField
            fullWidth
            label="Owner"
            value={owner}
            onChange={(e) => onOwnerChange(e.target.value)}
            sx={{
              ...tenantManagementTextFieldSx,
              gridColumn:
                showOperator && preferences.showOperator ? "1 / -1" : undefined,
            }}
          />
        )}
      </Box>
    </Box>
  );
};
